import './turnstile.css';

const SITE_KEY = '0x4AAAAAAEkWiCKNcylSnmGw';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SETTINGS_URL = 'https://tophamtechbe.com/api/treas/idx/v1/lead-verification';
let scriptPromise;
let queue = Promise.resolve();

export const isLeadVerificationRequired = async (siteId) => {
  if (!siteId) throw new Error('Unable to identify this site. Please reload and try again.');
  const response = await fetch(`${SETTINGS_URL}?${new URLSearchParams({ siteId })}`, {
    cache: 'no-store'
  });
  if (!response.ok) throw new Error('Verification settings could not load. Please try again.');
  const configuration = await response.json();
  if (typeof configuration.required !== 'boolean') {
    throw new Error('Verification settings could not load. Please try again.');
  }
  return configuration.required;
};

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const fail = () => {
      clearTimeout(timer);
      script.remove();
      scriptPromise = undefined;
      reject(new Error('Verification could not load. Please try again.'));
    };
    const timer = setTimeout(fail, 15000);
    script.src = SCRIPT_URL;
    script.async = true;
    script.onerror = fail;
    script.onload = () => {
      if (!window.turnstile) return fail();
      clearTimeout(timer);
      resolve(window.turnstile);
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

// A Turnstile response is single-use. Serialize challenges so concurrent lead
// forms can never share a widget or token.
const verify = (action) => new Promise((resolve, reject) => {
  const previousFocus = document.activeElement;
  const dialog = document.createElement('dialog');
  dialog.className = 'lead-verification';
  dialog.setAttribute('aria-labelledby', 'lead-verification-title');
  const title = document.createElement('h2');
  title.id = 'lead-verification-title';
  title.textContent = 'One quick check';
  const status = document.createElement('p');
  status.setAttribute('role', 'status');
  status.textContent = 'Verifying your request… If a checkbox appears, select it. Your request will continue automatically after verification.';
  const container = document.createElement('div');
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.textContent = 'Cancel';
  dialog.append(title, status, container, cancel);
  document.body.appendChild(dialog);
  let widget;
  let settled = false;
  const finish = (error, token) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    if (widget !== undefined) window.turnstile?.remove(widget);
    dialog.remove();
    if (previousFocus?.isConnected) previousFocus.focus();
    if (error) reject(error);
    else resolve(token);
  };
  const timer = setTimeout(
    () => finish(new Error('Verification timed out. Please try again.')),
    120000
  );
  cancel.onclick = () => finish(new Error('Verification cancelled. Your request has not been sent.'));
  dialog.oncancel = (event) => {
    event.preventDefault();
    cancel.click();
  };
  try {
    dialog.showModal();
  } catch {
    finish(new Error('Verification could not open. Please try another browser.'));
    return;
  }
  loadTurnstile().then((turnstile) => {
    if (settled) return;
    widget = turnstile.render(container, {
      sitekey: SITE_KEY,
      action,
      theme: 'light',
      size: 'flexible',
      'response-field': false,
      callback: (token) => finish(null, token),
      'error-callback': () => {
        finish(new Error('Verification failed. Please try again.'));
        return true;
      },
      'expired-callback': () => finish(new Error('Verification expired. Please try again.')),
      'timeout-callback': () => finish(new Error('Verification timed out. Please try again.')),
      'unsupported-callback': () => finish(new Error('Please use another browser to send your request.'))
    });
  }).catch((error) => finish(error));
});

export const requestLeadVerification = (action = 'lead_submit') => {
  if (!['lead_submit', 'otp_send'].includes(action)) return Promise.reject(new Error('Unsupported verification action.'));
  const request = queue.then(() => verify(action));
  queue = request.catch(() => {});
  return request;
};
